import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogIn, Mail, Lock, ArrowRight } from "lucide-react";

const LOGO_URL = "https://cdn.gamma.app/40wtxlv020nnuu2/0bd85e15bbd243ca96d4ed4e00a0f4fa/original/HumaDash-Logo-3.4.25.png";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center bg-[#F4F5F7]">
      <div className="absolute inset-0 hero-glow opacity-20 pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="HumaDash" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl text-slate-800">Welcome Back</h1>
          <p className="text-slate-500 mt-1">Sign in to your HumaDash account</p>
        </div>

        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600" data-testid="login-error">{error}</div>}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 border-slate-200" required data-testid="login-email" /></div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="pl-10 border-slate-200" required data-testid="login-password" /></div>
              </div>
              <Button type="submit" className="w-full bg-[#6B21A8] hover:bg-[#581C87] text-white" disabled={loading} data-testid="login-submit">
                {loading ? "Signing in..." : <><LogIn className="h-4 w-4 mr-2" />Sign In</>}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-500">
              Don't have an account?{" "}<Link to="/register" className="text-primary font-medium hover:underline">Create one</Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
